import { OrderState } from '@tcr-erp/shared';
import { useB2BCreditLimit } from '@/hooks/useB2BReceivables';
import type { TenantOrder } from '@/mocks/orders';
import type { B2BCreditLimit } from '@/services/b2b-receivable.service';

/**
 * Các trạng thái mà tiền của đơn CHƯA được ghi nhận vào `current_debt` (công nợ
 * kế toán do BE trả về). Đơn đã giao/hoàn tất thì cộng `summary.total` lần nữa là
 * đếm hai lần — số "vượt hạn mức" sẽ phồng gấp đôi. Giữ KHỚP với
 * `ORDER_STATES_TRUOC_KHI_GHI_NO` ở BE (`orders-aggregate.service.ts`).
 */
const TRANG_THAI_CHUA_GHI_NO: OrderState[] = [
  OrderState.DRAFT,
  OrderState.PENDING,
  OrderState.CONFIRMED,
];

export interface OrderCreditLimitInfo {
  /** Công nợ đang có của khách, chưa tính đơn đang xem. */
  currentDebt: number;
  /** Hạn mức hiệu lực (hạn mức riêng, trung bình 3 tháng, hoặc mặc định tenant). */
  creditLimit: number;
  /** Tổng tiền đơn đang xem; 0 khi tiền của đơn đã nằm trong `currentDebt`. */
  orderTotal: number;
  /** Công nợ dự kiến sau khi duyệt đơn này. */
  debtAfter: number;
  /** Số tiền vượt quá hạn mức; 0 khi còn trong hạn mức. */
  excess: number;
  /** Công nợ DỰ KIẾN vượt hạn mức — không phải `is_over_limit` của BE. */
  isOverLimit: boolean;
}

/**
 * TCR-1825 — dùng chung cho `CustomerCard` (trang chi tiết) và
 * `OrderSummaryCustomerCard` (pop-up xem nhanh), cùng nguồn `useB2BCreditLimit`
 * mà TCR-1862 (duyệt đơn) và TCR-1818 (chọn khách trong POS) đã dùng.
 *
 * TCR-1892 — trước đây hook chỉ trả dữ liệu khi `is_over_limit` của BE bật, mà cờ
 * đó chỉ so công nợ HIỆN TẠI với hạn mức. Khách nợ 0đ đặt đơn 302.400.000đ trên
 * hạn mức 90.516.520đ vẫn "trong hạn mức" theo cờ ⇒ màn chi tiết đơn không cảnh
 * báo gì. Giờ hook tự tính công nợ DỰ KIẾN (`current_debt` + tổng đơn đang xem) và
 * luôn trả số liệu cho khách B2B tổ chức để hiển thị, kèm `isOverLimit` theo số dự
 * kiến đó — cùng công thức `debtAfter > credit_limit` mà BE dùng lúc gắn cờ
 * `credit_approval_status` (`pos-order.service.ts`).
 */
export function useOrderCreditLimitWarning(
  // Trang chi tiết gọi hook trước khi `useOrderDetail` trả dữ liệu — hook phải chạy
  // vô điều kiện (rules of hooks) nên nhận luôn cả lúc đơn chưa có.
  order: TenantOrder | null | undefined,
): OrderCreditLimitInfo | null {
  const customerId = order?.user_type === 'organization' ? order.user_id ?? null : null;
  const { data } = useB2BCreditLimit(customerId);
  return computeOrderCreditLimitInfo(order, data);
}

/**
 * Phần tính thuần của `useOrderCreditLimitWarning` — TCR-2439 v3 dùng lại cho menu ⋮ ở danh sách
 * đơn (lấy hạn mức lúc bấm "Xác nhận", không qua hook).
 */
export function computeOrderCreditLimitInfo(
  order: TenantOrder | null | undefined,
  data: Pick<B2BCreditLimit, 'credit_limit' | 'current_debt' | 'is_unlimited'> | null | undefined,
): OrderCreditLimitInfo | null {
  // `is_unlimited` = tenant tắt công tắc kiểm tra hạn mức. Khi đó `credit_limit`
  // bằng 0 nhưng KHÔNG có khái niệm "vượt" — hiện số liệu ở đây là nói dối.
  if (!order || !data || data.is_unlimited) return null;
  // 2026-11-03 (PO) — khách kênh MT không xét hạn mức công nợ: không hiện cảnh báo/số liệu hạn mức.
  if (order.b2b_channel === 'MT') return null;

  const currentDebt = data.current_debt ?? 0;
  const creditLimit = data.credit_limit ?? 0;
  // TCR-2130 — đơn kênh GT khách trả trước 100%, tiền đơn không thành công nợ.
  const orderTotal = TRANG_THAI_CHUA_GHI_NO.includes(order.state) && order.b2b_channel !== 'GT'
    ? order.summary?.total ?? order.total_amount ?? 0
    : 0;
  const debtAfter = currentDebt + orderTotal;

  return {
    currentDebt,
    creditLimit,
    orderTotal,
    debtAfter,
    excess: Math.max(0, debtAfter - creditLimit),
    isOverLimit: debtAfter > creditLimit,
  };
}
